import { formatPeriod } from '../../utils/reportPeriod';
import type { ReportInterval } from '../../types/reports';

/**
 * Ladezustand der Vorjahreswerte, den der Loader in Plan 24-05 zurueckgibt (Vertrag zuerst).
 *
 * - `'off'`     -- Schalter aus, kein Ladevorgang.
 * - `'loading'` -- Aufruf laeuft (nicht blockierend, D-10 -- kein Ladeanzeiger).
 * - `'loaded'`  -- Aufruf erfolgreich, Vorjahresmeldung liegt vor.
 * - `'missing'` -- kein Treffer ab Status `submitted` fuer die Vorjahresperiode (D-06).
 * - `'error'`   -- Netzfehler oder 5xx (D-07).
 */
export type PreviousValuesLoadState = 'off' | 'loading' | 'loaded' | 'missing' | 'error';

const ERROR_TEXT = 'Die Vorjahreswerte konnten nicht geladen werden.';
const TOTALS_FAILED_TEXT = 'Die Vorjahres-Summen konnten nicht berechnet werden.';

/**
 * Leitet den Hinweistext unter dem Schalter aus dem Ladezustand ab (D-06/D-07/D-21). Alle drei
 * Saetze erscheinen an DERSELBEN Stelle (direkt unter dem Schalter, nicht als Benachrichtigung und
 * nicht unter dem Kopfbereich); der Schalter bleibt in allen drei Faellen angeschaltet.
 *
 * Bei D-06 und D-07 ist ein Aus/An der Weg, es erneut zu versuchen; BEIM TEILFEHLSCHLAG AUS D-21
 * AUSDRUECKLICH NICHT -- dessen Ergebnis wird gecacht, ein Aus/An laedt nichts nach, wer erneut
 * versuchen will, laedt die Seite neu. D-21 haelt diese Abweichung von D-07 ausdruecklich fest,
 * damit die Verifikation sie nicht als Widerspruch meldet.
 *
 * @param totalsFailed Der Teilfehlschlag aus D-21: bei Formular D ist der `GET` gelungen und die
 *   277 Feldwerte stehen, nur der zweite Aufruf (`POST /api/reports/calculate` fuer die 41 Summen)
 *   ist gescheitert. Optional und voreingestellt `false`, damit die vier Formulartypen ohne
 *   zweiten Aufruf ihn nicht mitgeben muessen. Wirkt ausschliesslich bei `'loaded'` -- bei
 *   `'error'` und `'missing'` gewinnt weiterhin deren eigener Satz (es gab nichts zu rechnen), bei
 *   `'off'` bleibt es bei `null`.
 */
export function previousValuesHintText(
  state: PreviousValuesLoadState,
  previousPeriod: string | null,
  interval: ReportInterval | null,
  totalsFailed = false
): string | null {
  switch (state) {
    case 'off':
    case 'loading':
      return null;
    case 'missing':
      if (!previousPeriod || !interval) {
        return null;
      }
      return `Für ${formatPeriod(previousPeriod, interval)} liegt keine eingereichte Meldung dieses Werks vor.`;
    case 'error':
      return ERROR_TEXT;
    case 'loaded':
      return totalsFailed ? TOTALS_FAILED_TEXT : null;
  }
}
